import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, SectionHeading } from '@/components/ui';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';

/**
 * A companion's availability. project.md §29 and §33.
 *
 * Two independent things, deliberately kept apart on the page because they
 * answer different questions:
 *
 *   PRESENCE   — "can someone call me right now?" Only affects live chat,
 *                voice and video.
 *   SCHEDULE   — "which hours do I meet people?" Only affects in-person
 *                bookings, which are made for a future date and do not care
 *                whether you are online this minute.
 *
 * Conflating them is the obvious mistake: it would either hide a companion
 * from tomorrow's calendar because they closed their laptop, or imply they are
 * ready to take a call because they work Saturdays.
 */
type Presence = 'offline' | 'online' | 'busy';

const PRESENCE: { value: Presence; label: string; note: string; tone: 'teal' | 'marigold' | 'neutral' }[] =
  [
    { value: 'online', label: 'Available', note: 'People can call you now', tone: 'teal' },
    { value: 'busy', label: 'Busy', note: 'Visible, but not taking calls', tone: 'marigold' },
    { value: 'offline', label: 'Offline', note: 'Not taking calls', tone: 'neutral' },
  ];

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

interface Slot {
  id: string;
  weekday: number;
  starts_at: string;
  ends_at: string;
}

export default function CompanionAvailability() {
  const { profile } = useAuth();

  const [presence, setPresence] = useState<Presence>('offline');
  const [acceptsScheduled, setAcceptsScheduled] = useState(true);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [day, setDay] = useState(6); // Saturday — the most common answer
  const [from, setFrom] = useState('10:00');
  const [to, setTo] = useState('18:00');

  const load = useCallback(async () => {
    if (!profile) return;

    const [{ data: cp }, { data: rows }] = await Promise.all([
      supabase
        .from('companion_profiles')
        .select('presence, accepts_scheduled')
        .eq('id', profile.id)
        .maybeSingle(),
      supabase
        .from('availability_slots')
        .select('id, weekday, starts_at, ends_at')
        .eq('companion_id', profile.id)
        .order('weekday'),
    ]);

    if (cp) {
      setPresence((cp.presence as Presence) ?? 'offline');
      setAcceptsScheduled((cp.accepts_scheduled as boolean) ?? true);
    }
    setSlots((rows as unknown as Slot[]) ?? []);
    setLoading(false);
  }, [profile]);

  useEffect(() => {
    void load();
  }, [load]);

  async function savePresence(next: Presence) {
    if (!profile) return;
    setPresence(next); // optimistic: a status toggle that lags feels broken
    const { error: err } = await supabase
      .from('companion_profiles')
      .update({ presence: next, presence_at: new Date().toISOString() })
      .eq('id', profile.id);

    if (err) {
      setError(err.message);
      await load(); // put the real value back
    }
  }

  async function toggleScheduled() {
    if (!profile) return;
    const next = !acceptsScheduled;
    setAcceptsScheduled(next);
    const { error: err } = await supabase
      .from('companion_profiles')
      .update({ accepts_scheduled: next })
      .eq('id', profile.id);
    if (err) {
      setError(err.message);
      await load();
    }
  }

  async function addSlot() {
    if (!profile) return;
    setError(null);

    if (to <= from) {
      setError('The end time has to be after the start time.');
      return;
    }

    const { error: err } = await supabase.from('availability_slots').insert({
      companion_id: profile.id,
      weekday: day,
      starts_at: from,
      ends_at: to,
    });

    // 23505 — the unique constraint. An exact duplicate is not worth an error
    // message; the row they wanted already exists.
    if (err && err.code !== '23505') setError(err.message);
    await load();
  }

  async function removeSlot(id: string) {
    await supabase.from('availability_slots').delete().eq('id', id);
    await load();
  }

  if (!profile) return null;

  const byDay = DAYS.map((_, i) => slots.filter((s) => s.weekday === i));

  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <header className="animate-rise mb-8">
        <p className="eyebrow mb-2">Your time</p>
        <h1 className="text-3xl font-semibold">Availability</h1>
      </header>

      {error && (
        <Card className="mb-6 px-5 py-4">
          <p className="text-sm" style={{ color: 'var(--danger)' }}>
            {error}
          </p>
        </Card>
      )}

      {/* ── Presence ──────────────────────────────────────────────────────── */}
      <Card className="animate-rise mb-8 p-6">
        <SectionHeading eyebrow="Right now">Calls</SectionHeading>
        <p className="-mt-3 mb-4 text-sm text-[var(--ink-2)]">
          Only affects chat, voice and video. Meeting in person is scheduled separately.
        </p>

        <div className="flex flex-col gap-2">
          {PRESENCE.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => void savePresence(p.value)}
              disabled={loading}
              className="flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-all duration-150 hover:-translate-y-px"
              style={{
                background: presence === p.value ? 'var(--marigold-soft)' : 'var(--surface)',
                borderColor: presence === p.value ? 'var(--marigold)' : 'var(--line)',
              }}
            >
              <span>
                <span className="block font-medium">{p.label}</span>
                <span className="block text-xs text-[var(--ink-2)]">{p.note}</span>
              </span>
              {presence === p.value && <Badge tone={p.tone}>Current</Badge>}
            </button>
          ))}
        </div>
      </Card>

      {/* ── Weekly schedule ───────────────────────────────────────────────── */}
      <Card className="animate-rise p-6">
        <SectionHeading eyebrow="In person">Weekly hours</SectionHeading>

        <label className="mb-5 flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            checked={acceptsScheduled}
            onChange={() => void toggleScheduled()}
            className="mt-0.5 size-4"
          />
          <span>
            <span className="font-medium">I take bookings to meet in person</span>
            <span className="block text-xs text-[var(--ink-2)]">
              Turn this off and you keep taking calls, but nobody can book you for a date.
            </span>
          </span>
        </label>

        {acceptsScheduled && (
          <>
            <div className="mb-5 flex flex-wrap items-end gap-3">
              <label className="text-sm">
                <span className="mb-1.5 block font-medium">Day</span>
                <select
                  value={day}
                  onChange={(e) => setDay(Number(e.target.value))}
                  className="rounded-lg px-3 py-2 text-sm"
                  style={{
                    background: 'var(--surface)',
                    color: 'var(--ink)',
                    border: '1px solid var(--line)',
                  }}
                >
                  {DAYS.map((d, i) => (
                    <option key={d} value={i}>
                      {d}
                    </option>
                  ))}
                </select>
              </label>

              <label className="text-sm">
                <span className="mb-1.5 block font-medium">From</span>
                <input
                  type="time"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  className="rounded-lg px-3 py-2 text-sm"
                  style={{
                    background: 'var(--surface)',
                    color: 'var(--ink)',
                    border: '1px solid var(--line)',
                  }}
                />
              </label>

              <label className="text-sm">
                <span className="mb-1.5 block font-medium">To</span>
                <input
                  type="time"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  className="rounded-lg px-3 py-2 text-sm"
                  style={{
                    background: 'var(--surface)',
                    color: 'var(--ink)',
                    border: '1px solid var(--line)',
                  }}
                />
              </label>

              <Button size="sm" onClick={() => void addSlot()}>
                Add
              </Button>
            </div>

            {slots.length === 0 ? (
              <p className="text-sm text-[var(--ink-2)]">
                No hours set. People can still request any time — you decide whether to accept.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {byDay.map((daySlots, i) =>
                  daySlots.length === 0 ? null : (
                    <li key={i} className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="w-24 shrink-0 font-medium">{DAYS[i]}</span>
                      {daySlots.map((s) => (
                        <span
                          key={s.id}
                          className="inline-flex items-center gap-2 rounded-lg px-2.5 py-1.5"
                          style={{ background: 'var(--surface-2)' }}
                        >
                          <span className="tabular-nums">
                            {s.starts_at.slice(0, 5)}–{s.ends_at.slice(0, 5)}
                          </span>
                          <button
                            type="button"
                            onClick={() => void removeSlot(s.id)}
                            aria-label="Remove"
                            className="text-[var(--muted)] transition-colors hover:text-[var(--danger)]"
                          >
                            ✕
                          </button>
                        </span>
                      ))}
                    </li>
                  ),
                )}
              </ul>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
